import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { environment } from 'src/environment/environment';
import { AnimalModel } from '../models/animal.model';
import { AnimalDocumentModel } from '../models/animal-document.model';
import { AnimalMediaCategoryModel } from '../models/animal-media-category.model';
import { AnimalDocumentCategory } from '../models/animal-document-category.enum';
import { AnimalDocumentsRepository } from '../states/animal-documents.repository';
import { AnimalDocumentService } from '../services/animal-document.service';
import { TokenService } from '../../../shared/services/token.service';
import { UiAnimalDocument } from './grid/documents-grid.component';
import { DocumentFilterState } from './header/documents-header.component';

@Component({
  selector: 'app-animal-details-documents',
  templateUrl: './animal-details-documents.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AnimalDetailsDocumentsComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | null = null;

  documents: UiAnimalDocument[] = [];
  categoryOptions: AnimalMediaCategoryModel[] = [];
  flippedItems: Record<number, boolean> = {};
  isMultiSelectMode = false;
  selectedIds = new Set<number>();

  private readonly destroy$ = new Subject<void>();

  filter: DocumentFilterState = {
    showUpload: true,
    showGenerated: true,
    selectedCategories: [] as AnimalDocumentCategory[]
  };

  constructor(
    private readonly _documentsRepository: AnimalDocumentsRepository,
    private readonly _documentService: AnimalDocumentService,
    private readonly _cdr: ChangeDetectorRef,
    public readonly _translateService: TranslocoService,
    private readonly _confirmationService: ConfirmationService,
    private readonly _tokenService: TokenService
  ) {
  }

  ngOnInit(): void {
    if (!this.animal) return;

    this.categoryOptions = this._documentService.getAnimalDocumentCategories();

    this._documentsRepository.getDocuments(this.animal.id)
      .pipe(take(1))
      .subscribe();

    this._documentsRepository.selectDocuments(this.animal.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((documents) => {
        this.documents = documents.map((item) => this.toUiDocument(item));
        this._cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toUiDocument(item: AnimalDocumentModel): UiAnimalDocument {
    return {
      ...item,
      uiDate: item.date ? new Date(item.date) : null
    };
  }

  onFilterChange(): void {
    this._cdr.markForCheck();
  }

  get filteredDocuments(): UiAnimalDocument[] {
    return this.documents.filter((item) => {
      const isUpload = item.sourceType === 'upload';
      const passesSource =
        (this.filter.showUpload && isUpload) || (this.filter.showGenerated && !isUpload);

      const passesCategory =
        this.filter.selectedCategories.length === 0 ||
        this.filter.selectedCategories.includes(item.category);

      return passesSource && passesCategory;
    });
  }

  toggleFlip(id: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.isMultiSelectMode) return;

    this.flippedItems[id] = !this.flippedItems[id];
    Object.keys(this.flippedItems).forEach(key => {
      if (key !== id.toString()) {
        this.flippedItems[+key] = false;
      }
    });
    this._cdr.markForCheck();
  }

  multiSelectMode(): void {
    this.isMultiSelectMode = !this.isMultiSelectMode;
    if (this.isMultiSelectMode) {
      Object.keys(this.flippedItems).forEach(key => {
        this.flippedItems[+key] = false;
      });
    } else {
      this.selectedIds.clear();
    }
    this._cdr.markForCheck();
  }

  toggleSelect(id: number): void {
    if (!this.isMultiSelectMode) return;
    this.selectedIds.has(id) ? this.selectedIds.delete(id) : this.selectedIds.add(id);
    this._cdr.markForCheck();
  }

  resetMultiSelectMode(): void {
    this.isMultiSelectMode = false;
    this.selectedIds.clear();
    this._cdr.markForCheck();
  }

  onCategoryChange(document: AnimalDocumentModel): void {
    if (!this.animal || document.sourceType !== 'upload') return;
    this._documentsRepository
      .updateDocument(document.id, this.buildMetaFormData(document), this.animal.id)
      .pipe(take(1))
      .subscribe(() => this._cdr.markForCheck());
  }

  onDateChange(document: UiAnimalDocument): void {
    if (!this.animal || document.sourceType !== 'upload') return;
    document.date = document.uiDate ? this.toIsoDate(document.uiDate) : null;
    this._documentsRepository
      .updateDocument(document.id, this.buildMetaFormData(document), this.animal.id)
      .pipe(take(1))
      .subscribe(() => this._cdr.markForCheck());
  }

  onReplaceFile(event: { document: AnimalDocumentModel, file: File }): void {
    if (!this.animal || event.document.sourceType !== 'upload') return;
    const formData = this.buildMetaFormData(event.document);
    formData.append('file', event.file);
    this._documentsRepository
      .updateDocument(event.document.id, formData, this.animal.id)
      .pipe(take(1))
      .subscribe((updated) => {
        this.documents = this.documents.map((d) => d.id === updated.id ? this.toUiDocument(updated) : d);
        this._cdr.markForCheck();
      });
  }

  deleteDocument(document: AnimalDocumentModel): void {
    if (document.sourceType !== 'upload') return;
    this._confirmationService.confirm({
      message: this._translateService.translate('delete_confirmation'),
      acceptLabel: this._translateService.translate('yes'),
      rejectLabel: this._translateService.translate('no'),
      header: '',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        if (!this.animal) return;
        this._documentsRepository.deleteDocument(document.id, this.animal.id)
          .pipe(take(1))
          .subscribe(() => {
            this.documents = this.documents.filter((d) => d.id !== document.id);
            this.selectedIds.delete(document.id);
            this._cdr.markForCheck();
          });
      }
    });
  }

  deleteSelected(): void {
    if (!this.animal || this.selectedIds.size === 0) return;

    const deletable = this.documents.filter(
      (d) => this.selectedIds.has(d.id) && d.sourceType === 'upload'
    );

    if (deletable.length === 0) {
      this.resetMultiSelectMode();
      return;
    }

    this._confirmationService.confirm({
      message: this._translateService.translate('delete_confirmation'),
      acceptLabel: this._translateService.translate('yes'),
      rejectLabel: this._translateService.translate('no'),
      header: '',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        deletable.forEach((document) => {
          this._documentsRepository.deleteDocument(document.id, this.animal!.id)
            .pipe(take(1))
            .subscribe(() => {
              this.documents = this.documents.filter((d) => d.id !== document.id);
              this.selectedIds.delete(document.id);
              this.resetMultiSelectMode();
              this._cdr.markForCheck();
            });
        });
      }
    });
  }

  downloadSingle(document: AnimalDocumentModel): void {
    this.download(document);
  }

  downloadSelected(): void {
    if (this.selectedIds.size === 0) return;
    const selected = this.documents.filter((d) => this.selectedIds.has(d.id));
    selected.forEach((document, idx) => {
      setTimeout(() => this.download(document), idx * 300);
    });
    this.resetMultiSelectMode();
  }

  private async download(document: AnimalDocumentModel): Promise<void> {
    if (!document.downloadUrl) return;
    const token = this._tokenService.getAccessToken();
    if (!token) return;

    const origin = environment.apiUrl.replace(/\/api\/?$/, '');
    const url = `${origin}${document.downloadUrl}?x-auth-token=${encodeURIComponent(token)}`;
    const filename = this.fileName(document);

    try {
      const response = await fetch(url, {
        headers: { 'x-auth-token': token }
      });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();

      const a = window.document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('Download fehlgeschlagen', e);
    }
  }

  private fileName(document: AnimalDocumentModel): string {
    return document.filePath?.split('/').pop() ?? `document_${document.id}.pdf`;
  }

  private buildMetaFormData(document: AnimalDocumentModel): FormData {
    const formData = new FormData();
    formData.append('animal_id', this.animal!.id.toString());
    formData.append('category', document.category);
    if (document.date) {
      formData.append('date', document.date);
    }
    return formData;
  }

  private toIsoDate(date: Date): string {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return utc.toISOString();
  }
}
